import { Link } from 'react-router';

import { Icon, type IconName } from './Icon';

/** The kinds of record the product links to. */
export type RecordKind = 'customer' | 'job' | 'deal' | 'invoice' | 'lead' | 'proposal';

const RECORD_ROUTE: Record<RecordKind, string> = {
  customer: '/customers',
  job: '/jobs',
  deal: '/pipeline',
  invoice: '/invoices',
  lead: '/leads',
  proposal: '/proposals',
};

const RECORD_ICON: Record<RecordKind, IconName> = {
  customer: 'customers',
  job: 'jobs',
  deal: 'pipeline',
  invoice: 'invoicing',
  lead: 'leads',
  proposal: 'proposals',
};

/**
 * Where a record lives.
 *
 * For code that navigates without rendering a link, such as a row that opens
 * its record when activated.
 *
 * @param kind which kind of record
 * @param id the record's id
 * @returns the app path
 */
export function recordPath(kind: RecordKind, id: string): string {
  return `${RECORD_ROUTE[kind]}/${encodeURIComponent(id)}`;
}

export interface RecordLinkProps {
  readonly kind: RecordKind;
  /** The record's id. */
  readonly id: string;
  /** What to call it: a name or a number, as the person knows it, such as "Dana Ruiz" or "Job 4821". */
  readonly label: string;
}

/**
 * A link to a record, drawn the same way wherever one appears.
 *
 * A job named in a customer's history, a deal named on Today and an invoice
 * named in a payment are the same kind of thing to the person reading: a place
 * they can go. One component means one route per kind, one look, and no page
 * rebuilding `'/jobs/' + job.id` and getting a trailing slash or an unencoded
 * id wrong. The glyph says what kind of record it is without adding a word to
 * the label, and is hidden from assistive technology, which already has the label.
 *
 * Needs a router.
 *
 * @param props see {@link RecordLinkProps}
 * @returns the link
 */
export function RecordLink({ kind, id, label }: RecordLinkProps): React.JSX.Element {
  return (
    <Link to={recordPath(kind, id)} className="fo-record-link" data-kind={kind}>
      <Icon name={RECORD_ICON[kind]} className="fo-record-link__icon" />
      <span>{label}</span>
    </Link>
  );
}
